import React, { useMemo, useRef, createContext, useContext } from "react";
import { useQuery } from "@apollo/client/react";

import {
  CustomFieldsListDataDocument,
  GetIssueSourcesDocument,
  GetMyPreferencesDocument,
  GetReasonsDocument,
  IssueTypeListDocument,
} from "#app/graphql/generated/graphql";
import { connectionRows } from "./entities/types";
import { useAppData } from "#app/lib/data/app-provider";
import { compareStages } from "#app/lib/stage-order";
import type {
  CustomFieldRow as CustomField,
  FavoriteRef as Favorite,
  IssueSourceRow as IssueSource,
  ReasonRow as Reason,
  WorkspaceIssueType as IssueType,
  WorkspaceMember as User,
  WorkspaceStage as Stage,
  WorkspaceTeam as Team,
  WorkspaceWorkflow as Workflow,
} from "./entities/rows";

// ── Types ────────────────────────────────────────────────────────────────────
//
// Row aliases, not per-file `Query["field"]["edges"][number]["node"]`
// extractions (R3): these are the same aliases `useAppData()` hands out, so
// the two providers read ONE query and agree on its shape by construction, and
// nothing here casts.

export type {
  Workflow as WorkspaceWorkflow,
  Stage as WorkspaceStage,
  Team as WorkspaceTeam,
  User as WorkspaceUser,
  Reason as WorkspaceReason,
  CustomField as WorkspaceCustomField,
  IssueType as WorkspaceIssueType,
};

export type WorkspaceDataState = {
  workflows: Workflow[];
  stages: Stage[];
  teams: Team[];
  users: User[];
  reasons: Reason[];
  customFields: CustomField[];
  issueTypes: IssueType[];
  issueSources: IssueSource[];
  workflowMap: Map<string, Workflow>;
  stageMap: Map<string, Stage>;
  stagesByWorkflow: Map<string, Stage[]>;
  userMap: Map<string, User>;
  teamMap: Map<string, Team>;
  issueTypeMap: Map<string, IssueType>;
  issueSourceMap: Map<string, IssueSource>;
  favoriteIds: Set<string>;
  /** True once the preferences query has resolved — an empty `favoriteIds`
   * means "none" only when this is set, not "not loaded yet". */
  favoritesKnown: boolean;
  issueSourcesKnown: boolean;
  userId: string | undefined;
  teamIds: string[];
  isHydrated: boolean;
};

// ── Constants ───────────────────────────────────────────────────────────────

const REASONS_VARS = { limit: 500 } as const;
/** The one page the workspace reads every custom field in; the manage
 *  dialog's list hook asks with the same variables so both share a cache
 *  entry and one refetch reaches both. */
export const CUSTOM_FIELDS_VARS = { limit: 200 } as const;
// The whole pool, as Settings › Sources loads it: a workspace seeds 55, so a
// page of 50 hid the tail of the list from every Source picker.
const ISSUE_SOURCES_VARS = { limit: 500, orderBy: { name: 1 } } as const;
const CACHE_FIRST = { fetchPolicy: "cache-first" as const };

// ── Context ─────────────────────────────────────────────────────────────────

const WorkspaceDataContext = createContext<WorkspaceDataState | null>(null);

// ── Provider ────────────────────────────────────────────────────────────────

export function WorkspaceDataProvider({ children }: { children: React.ReactNode }) {
  const { currentUser, workflows: rawWorkflows, teams: rawTeams, members, stages: rawStages } = useAppData();

  // Skip until AppProvider has resolved auth + workspace data. Nothing
  // prefetches these into cache, so without the gate the queries would fire
  // before auth boot completes, sending requests without an Authorization
  // header and causing UNAUTHENTICATED errors.
  const skip = !currentUser;
  const { data: prefsData } = useQuery(GetMyPreferencesDocument, { ...CACHE_FIRST, skip });
  const { data: reasonsData } = useQuery(GetReasonsDocument, { ...CACHE_FIRST, variables: REASONS_VARS, skip });
  const { data: fieldsData } = useQuery(CustomFieldsListDataDocument, { ...CACHE_FIRST, variables: CUSTOM_FIELDS_VARS, skip });
  const { data: typesData } = useQuery(IssueTypeListDocument, { ...CACHE_FIRST, skip });
  const { data: sourcesData } = useQuery(GetIssueSourcesDocument, {
    ...CACHE_FIRST,
    variables: ISSUE_SOURCES_VARS,
    skip,
  });

  // Cluster A: workflow/stage topology
  const topologyMemo = useMemo(() => {
    const workflows = rawWorkflows ?? [];
    const stages = rawStages ?? [];

    const workflowMap = new Map<string, Workflow>();
    for (const w of workflows) workflowMap.set(w.workflowId, w);

    const stageMap = new Map<string, Stage>();
    for (const s of stages) stageMap.set(s.stageId, s);

    const stagesByWorkflow = new Map<string, Stage[]>();
    for (const s of stages) {
      // apiv2 types workflowId as nullable; mongo data always has it set,
      // but defend against the type — drop orphan stages from the map.
      if (!s.workflowId) continue;
      const list = stagesByWorkflow.get(s.workflowId);
      if (list) list.push(s);
      else stagesByWorkflow.set(s.workflowId, [s]);
    }
    for (const list of stagesByWorkflow.values()) {
      list.sort(compareStages);
    }

    const isHydrated = workflows.length > 0 || stages.length > 0;
    return { workflows, stages, workflowMap, stageMap, stagesByWorkflow, isHydrated };
  }, [rawWorkflows, rawStages]);

  // Cluster B: user identity
  const userMemo = useMemo(() => {
    const teams = rawTeams ?? [];
    const users = members ?? [];

    const userMap = new Map<string, User>();
    for (const u of users) userMap.set(u.userId, u);

    const teamMap = new Map<string, Team>();
    for (const t of teams) teamMap.set(t.teamId, t);

    const userId = currentUser?.userId;
    // `viewerRole` is non-null exactly when the viewer is on the team, in
    // either role — the server answers it, so no id scan happens here.
    const teamIds = teams.filter((t) => t.viewerRole != null).map((t) => t.teamId);
    return { teams, users, userMap, teamMap, userId, teamIds };
  }, [rawTeams, members, currentUser]);

  // Cluster C: favorites — stabilize reference when contents haven't changed
  const favoriteIdsRef = useRef<Set<string>>(new Set());
  const favoriteIds = useMemo(() => {
    const nextIds = new Set<string>(
      (prefsData?.myPreferences?.favorites ?? [])
        .filter((f: Favorite) => f.type === "issue")
        .map((f: Favorite) => f.referenceId),
    );
    const prev = favoriteIdsRef.current;
    if (nextIds.size === prev.size && [...nextIds].every((id) => prev.has(id))) {
      return prev;
    }
    favoriteIdsRef.current = nextIds;
    return nextIds;
  }, [prefsData]);
  const favoritesKnown = prefsData !== undefined;

  // Cluster D: supplementary data
  const supplementaryMemo = useMemo(() => {
    const issueTypes = connectionRows<IssueType>(typesData?.issueTypes);
    const issueTypeMap = new Map<string, IssueType>();
    for (const t of issueTypes) issueTypeMap.set(t.issueTypeId, t);
    const issueSources = connectionRows<IssueSource>(sourcesData?.issueSources);
    const issueSourceMap = new Map<string, IssueSource>();
    for (const source of issueSources) {
      issueSourceMap.set(source.issueSourceId, source);
    }
    return {
      reasons: connectionRows<Reason>(reasonsData?.reasons),
      customFields: connectionRows<CustomField>(fieldsData?.customFields),
      issueTypes,
      issueTypeMap,
      issueSources,
      issueSourceMap,
      issueSourcesKnown: sourcesData !== undefined,
    };
  }, [reasonsData, fieldsData, typesData, sourcesData]);

  // Assembly
  const state = useMemo<WorkspaceDataState>(() => ({
    ...topologyMemo,
    ...userMemo,
    favoriteIds,
    favoritesKnown,
    ...supplementaryMemo,
  }), [topologyMemo, userMemo, favoriteIds, favoritesKnown, supplementaryMemo]);

  return (
    <WorkspaceDataContext.Provider value={state}>
      {children}
    </WorkspaceDataContext.Provider>
  );
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useWorkspaceData(): WorkspaceDataState {
  const ctx = useContext(WorkspaceDataContext);
  if (!ctx) {
    throw new Error("useWorkspaceData must be used within WorkspaceDataProvider");
  }
  return ctx;
}
